
// services/api.js - React.js
// ============================================================
// Shared Axios API Service
// Supports:
//   1. Normal user/provider authentication
//   2. Admin authentication
//
// IMPORTANT:
// Admin tokens:
//   admin_access_token
//   admin_refresh_token
//   admin_user_data
//   admin_user_type
//
// User/provider tokens:
//   access_token
//   refresh_token
//   user_data
// ============================================================

import axios from 'axios';
import API_CONFIG from '../../config/env';

// ============================================================
// BASE URL
// ============================================================

const getBaseUrl = () => {
  if (API_CONFIG.BASE_URL) {
    console.log(
      '[API] Using BASE_URL from config:',
      API_CONFIG.BASE_URL
    );

    return API_CONFIG.BASE_URL;
  }

  const protocol = API_CONFIG.USE_SSL ? 'https' : 'http';

  const port =
    API_CONFIG.PORT &&
    ![80, 443].includes(parseInt(API_CONFIG.PORT, 10))
      ? `:${API_CONFIG.PORT}`
      : '';

  return `${protocol}://${API_CONFIG.SERVER_IP}${port}${API_CONFIG.BASE_PATH}`;
};

const API_BASE_URL = getBaseUrl();

console.log('[API] ===============================');
console.log('[API] Base URL:', API_BASE_URL);
console.log('[API] Environment:', process.env.NODE_ENV);
console.log('[API] Server IP:', API_CONFIG.SERVER_IP);
console.log('[API] Port:', API_CONFIG.PORT);
console.log('[API] SSL:', API_CONFIG.USE_SSL);
console.log('[API] Base Path:', API_CONFIG.BASE_PATH);
console.log('[API] ===============================');

// ============================================================
// TOKEN KEYS
// ============================================================

const TOKEN_KEYS = {
  // Normal user/provider authentication
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',

  // Admin authentication
  ADMIN_ACCESS_TOKEN: 'admin_access_token',
  ADMIN_REFRESH_TOKEN: 'admin_refresh_token',
  ADMIN_USER_DATA: 'admin_user_data',
  ADMIN_TOKEN_EXPIRY: 'admin_token_expiry',
  ADMIN_USER_TYPE: 'admin_user_type',
};

// ============================================================
// AXIOS INSTANCE
// ============================================================

const createAPIInstance = (baseURL, config = {}) => {
  return axios.create({
    baseURL,
    timeout: API_CONFIG.TIMEOUT || 30000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...config.headers,
    },
    ...config,
  });
};

const api = createAPIInstance(API_BASE_URL);

// ============================================================
// AUTHENTICATION TYPE DETECTION
// ============================================================

const authHelpers = {
  /**
   * Determine whether an admin session exists.
   *
   * Admin authentication is intentionally checked first.
   */
  isAdminSession: () => {
    try {
      const adminAccessToken = localStorage.getItem(
        TOKEN_KEYS.ADMIN_ACCESS_TOKEN
      );

      const adminUserType = localStorage.getItem(
        TOKEN_KEYS.ADMIN_USER_TYPE
      );

      const adminUserData = localStorage.getItem(
        TOKEN_KEYS.ADMIN_USER_DATA
      );

      return Boolean(
        adminAccessToken &&
        adminUserType === 'admin' &&
        adminUserData
      );
    } catch (error) {
      console.error(
        '[API] Failed to detect admin session:',
        error
      );

      return false;
    }
  },

  /**
   * Determine whether a normal user/provider session exists.
   */
  isUserSession: () => {
    try {
      const accessToken = localStorage.getItem(
        TOKEN_KEYS.ACCESS_TOKEN
      );

      const userData = localStorage.getItem(
        TOKEN_KEYS.USER_DATA
      );

      return Boolean(accessToken || userData);
    } catch (error) {
      console.error(
        '[API] Failed to detect user session:',
        error
      );

      return false;
    }
  },

  /**
   * Get current authentication type.
   *
   * Returns:
   *   'admin'
   *   'user'
   *   null
   */
  getAuthType: () => {
    if (authHelpers.isAdminSession()) {
      return 'admin';
    }

    if (authHelpers.isUserSession()) {
      return 'user';
    }

    return null;
  },

  /**
   * Get the correct access token for the current session.
   */
  getAccessToken: () => {
    try {
      if (authHelpers.isAdminSession()) {
        return localStorage.getItem(
          TOKEN_KEYS.ADMIN_ACCESS_TOKEN
        );
      }

      return localStorage.getItem(
        TOKEN_KEYS.ACCESS_TOKEN
      );
    } catch (error) {
      console.error(
        '[API] Failed to get access token:',
        error
      );

      return null;
    }
  },

  /**
   * Get the correct refresh token for the current session.
   */
  getRefreshToken: () => {
    try {
      if (authHelpers.isAdminSession()) {
        return localStorage.getItem(
          TOKEN_KEYS.ADMIN_REFRESH_TOKEN
        );
      }

      return localStorage.getItem(
        TOKEN_KEYS.REFRESH_TOKEN
      );
    } catch (error) {
      console.error(
        '[API] Failed to get refresh token:',
        error
      );

      return null;
    }
  },

  /**
   * Get current user/admin data.
   */
  getUserData: () => {
    try {
      if (authHelpers.isAdminSession()) {
        const data = localStorage.getItem(
          TOKEN_KEYS.ADMIN_USER_DATA
        );

        return data ? JSON.parse(data) : null;
      }

      const data = localStorage.getItem(
        TOKEN_KEYS.USER_DATA
      );

      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(
        '[API] Failed to get user data:',
        error
      );

      return null;
    }
  },

  /**
   * Save tokens to the correct authentication storage.
   */
  setTokens: (
    accessToken,
    refreshToken = null,
    authType = null
  ) => {
    try {
      const type =
        authType || authHelpers.getAuthType();

      if (type === 'admin') {
        if (accessToken) {
          localStorage.setItem(
            TOKEN_KEYS.ADMIN_ACCESS_TOKEN,
            accessToken
          );
        }

        if (refreshToken) {
          localStorage.setItem(
            TOKEN_KEYS.ADMIN_REFRESH_TOKEN,
            refreshToken
          );
        }

        console.log(
          '[API] Admin tokens saved successfully'
        );

        return;
      }

      if (accessToken) {
        localStorage.setItem(
          TOKEN_KEYS.ACCESS_TOKEN,
          accessToken
        );
      }

      if (refreshToken) {
        localStorage.setItem(
          TOKEN_KEYS.REFRESH_TOKEN,
          refreshToken
        );
      }

      console.log(
        '[API] User/provider tokens saved successfully'
      );
    } catch (error) {
      console.error(
        '[API] Failed to save tokens:',
        error
      );
    }
  },

  /**
   * Save user/admin data.
   */
  setUserData: (userData, authType = null) => {
    try {
      if (!userData) {
        return;
      }

      const type =
        authType || authHelpers.getAuthType();

      if (type === 'admin') {
        localStorage.setItem(
          TOKEN_KEYS.ADMIN_USER_DATA,
          JSON.stringify(userData)
        );

        return;
      }

      localStorage.setItem(
        TOKEN_KEYS.USER_DATA,
        JSON.stringify(userData)
      );
    } catch (error) {
      console.error(
        '[API] Failed to save user data:',
        error
      );
    }
  },

  /**
   * Clear ONLY normal user/provider authentication.
   */
  clearUserTokens: () => {
    try {
      localStorage.removeItem(
        TOKEN_KEYS.ACCESS_TOKEN
      );

      localStorage.removeItem(
        TOKEN_KEYS.REFRESH_TOKEN
      );

      localStorage.removeItem(
        TOKEN_KEYS.USER_DATA
      );

      console.log(
        '[API] User/provider tokens cleared'
      );
    } catch (error) {
      console.error(
        '[API] Failed to clear user tokens:',
        error
      );
    }
  },

  /**
   * Clear ONLY admin authentication.
   */
  clearAdminTokens: () => {
    try {
      localStorage.removeItem(
        TOKEN_KEYS.ADMIN_ACCESS_TOKEN
      );

      localStorage.removeItem(
        TOKEN_KEYS.ADMIN_REFRESH_TOKEN
      );

      localStorage.removeItem(
        TOKEN_KEYS.ADMIN_USER_DATA
      );

      localStorage.removeItem(
        TOKEN_KEYS.ADMIN_TOKEN_EXPIRY
      );

      localStorage.removeItem(
        TOKEN_KEYS.ADMIN_USER_TYPE
      );

      console.log(
        '[API] Admin tokens cleared'
      );
    } catch (error) {
      console.error(
        '[API] Failed to clear admin tokens:',
        error
      );
    }
  },

  /**
   * Clear authentication according to authentication type.
   */
  clearAuth: (authType = null) => {
    const type =
      authType || authHelpers.getAuthType();

    if (type === 'admin') {
      authHelpers.clearAdminTokens();
    } else if (type === 'user') {
      authHelpers.clearUserTokens();
    }
  },
};

// ============================================================
// IMAGE URL HELPER
// ============================================================

export const getImageUrl = (imagePath) => {
  if (!imagePath) {
    return null;
  }

  if (
    imagePath.startsWith('http://') ||
    imagePath.startsWith('https://')
  ) {
    return imagePath;
  }

  const baseUrl = API_CONFIG.SERVER_URL;

  const normalizedPath = imagePath.startsWith('/')
    ? imagePath
    : `/${imagePath}`;

  return `${baseUrl}${normalizedPath}`;
};

// ============================================================
// FULL URL HELPER
// ============================================================

export const getFullUrl = (path) => {
  if (!path) {
    return API_CONFIG.BASE_URL;
  }

  const base = API_CONFIG.BASE_URL;

  const normalizedPath = path.startsWith('/')
    ? path
    : `/${path}`;

  return `${base}${normalizedPath}`;
};

// ============================================================
// TOKEN HELPERS
// ============================================================

export const tokenHelpers = {
  getAccessToken: () => {
    return authHelpers.getAccessToken();
  },

  getRefreshToken: () => {
    return authHelpers.getRefreshToken();
  },

  getUserData: () => {
    return authHelpers.getUserData();
  },

  setTokens: (
    accessToken,
    refreshToken = null,
    authType = null
  ) => {
    authHelpers.setTokens(
      accessToken,
      refreshToken,
      authType
    );
  },

  setUserData: (
    userData,
    authType = null
  ) => {
    authHelpers.setUserData(
      userData,
      authType
    );
  },

  clearTokens: (authType = null) => {
    authHelpers.clearAuth(authType);
  },

  clearUserTokens: () => {
    authHelpers.clearUserTokens();
  },

  clearAdminTokens: () => {
    authHelpers.clearAdminTokens();
  },

  getAuthType: () => {
    return authHelpers.getAuthType();
  },

  isAdminSession: () => {
    return authHelpers.isAdminSession();
  },

  isUserSession: () => {
    return authHelpers.isUserSession();
  },
};

// ============================================================
// REQUEST INTERCEPTOR
// ============================================================

api.interceptors.request.use(
  (config) => {
    try {
      const authType =
        authHelpers.getAuthType();

      const token =
        authHelpers.getAccessToken();

      console.log('[API] Request →', {
        url: `${config.baseURL}${config.url}`,
        method: config.method?.toUpperCase(),
        authType,
        timestamp: new Date().toISOString(),
      });

      if (token) {
        config.headers =
          config.headers || {};

        config.headers.Authorization =
          `Bearer ${token}`;

        console.log(
          `[API] ${authType || 'unknown'} authentication token attached`
        );
      } else {
        console.log(
          '[API] No authentication token found'
        );
      }

      return config;
    } catch (error) {
      console.error(
        '[API] Failed to attach authentication token:',
        error
      );

      return config;
    }
  },

  (error) => {
    console.error(
      '[API] Request interceptor error:',
      error
    );

    return Promise.reject(error);
  }
);

// ============================================================
// RESPONSE INTERCEPTOR
// ============================================================

api.interceptors.response.use(
  (response) => {
    console.log('[API] Response ←', {
      url: response.config?.url,
      status: response.status,
      timestamp: new Date().toISOString(),
    });

    return response;
  },

  async (error) => {
    const originalRequest =
      error.config || {};

    const status =
      error.response?.status;

    console.error('[API] Response error:', {
      url: originalRequest?.url,
      status,
      message: error.message,
      data: error.response?.data,
    });

    // ========================================================
    // ONLY HANDLE 401
    // ========================================================

    if (
      status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const authType =
        authHelpers.getAuthType();

      console.log(
        '[API] 401 detected. Authentication type:',
        authType
      );

      // ======================================================
      // ADMIN SESSION
      // ======================================================

      if (authType === 'admin') {
        const adminRefreshToken =
          localStorage.getItem(
            TOKEN_KEYS.ADMIN_REFRESH_TOKEN
          );

        if (adminRefreshToken) {
          try {
            console.log(
              '[API] Refreshing ADMIN token...'
            );

            const refreshResponse =
              await axios.post(
                `${API_BASE_URL}/admin/refresh-token`,
                {
                  refreshToken:
                    adminRefreshToken,
                },
                {
                  headers: {
                    'Content-Type':
                      'application/json',
                    Accept:
                      'application/json',
                  },
                }
              );

            if (
              refreshResponse.data?.success
            ) {
              const refreshData =
                refreshResponse.data?.data ||
                {};

              const newAccessToken =
                refreshData.accessToken ||
                refreshResponse.data?.accessToken;

              const newRefreshToken =
                refreshData.refreshToken ||
                adminRefreshToken;

              if (newAccessToken) {
                localStorage.setItem(
                  TOKEN_KEYS.ADMIN_ACCESS_TOKEN,
                  newAccessToken
                );

                if (newRefreshToken) {
                  localStorage.setItem(
                    TOKEN_KEYS.ADMIN_REFRESH_TOKEN,
                    newRefreshToken
                  );
                }

                localStorage.setItem(
                  TOKEN_KEYS.ADMIN_TOKEN_EXPIRY,
                  String(
                    Date.now() +
                      7 * 24 * 60 * 60 * 1000
                  )
                );

                originalRequest.headers =
                  originalRequest.headers || {};

                originalRequest.headers.Authorization =
                  `Bearer ${newAccessToken}`;

                console.log(
                  '[API] ADMIN token refreshed successfully'
                );

                return api(
                  originalRequest
                );
              }
            }

            console.error(
              '[API] Admin refresh response did not contain a valid token'
            );
          } catch (refreshError) {
            console.error(
              '[API] ADMIN token refresh failed:',
              refreshError.response?.status,
              refreshError.response?.data ||
                refreshError.message
            );
          }
        }

        // ----------------------------------------------------
        // IMPORTANT:
        // Only clear ADMIN credentials.
        // Never touch provider/user tokens here.
        // ----------------------------------------------------

        console.log(
          '[API] Admin authentication failed. Clearing ADMIN session only.'
        );

        authHelpers.clearAdminTokens();

        window.dispatchEvent(
          new CustomEvent(
            'admin:logout'
          )
        );

        return Promise.reject(error);
      }

      // ======================================================
      // USER / PROVIDER SESSION
      // ======================================================

      if (authType === 'user') {
        const refreshToken =
          localStorage.getItem(
            TOKEN_KEYS.REFRESH_TOKEN
          );

        if (refreshToken) {
          try {
            console.log(
              '[API] Refreshing USER/PROVIDER token...'
            );

            const refreshResponse =
              await axios.post(
                `${API_BASE_URL}/auth/refresh-token`,
                {
                  refreshToken,
                },
                {
                  headers: {
                    'Content-Type':
                      'application/json',
                    Accept:
                      'application/json',
                  },
                }
              );

            if (
              refreshResponse.data?.success
            ) {
              const refreshData =
                refreshResponse.data?.data ||
                {};

              const newAccessToken =
                refreshData.accessToken ||
                refreshResponse.data?.accessToken;

              const newRefreshToken =
                refreshData.refreshToken ||
                refreshToken;

              if (newAccessToken) {
                localStorage.setItem(
                  TOKEN_KEYS.ACCESS_TOKEN,
                  newAccessToken
                );

                if (newRefreshToken) {
                  localStorage.setItem(
                    TOKEN_KEYS.REFRESH_TOKEN,
                    newRefreshToken
                  );
                }

                originalRequest.headers =
                  originalRequest.headers || {};

                originalRequest.headers.Authorization =
                  `Bearer ${newAccessToken}`;

                console.log(
                  '[API] USER/PROVIDER token refreshed successfully'
                );

                return api(
                  originalRequest
                );
              }
            }

            console.error(
              '[API] User refresh response did not contain a valid token'
            );
          } catch (refreshError) {
            console.error(
              '[API] USER/PROVIDER token refresh failed:',
              refreshError.response?.status,
              refreshError.response?.data ||
                refreshError.message
            );
          }
        }

        // ----------------------------------------------------
        // IMPORTANT:
        // Only clear USER/PROVIDER credentials.
        // Never touch admin credentials here.
        // ----------------------------------------------------

        console.log(
          '[API] User/provider authentication failed. Clearing USER session only.'
        );

        authHelpers.clearUserTokens();

        window.dispatchEvent(
          new CustomEvent(
            'auth:logout'
          )
        );

        return Promise.reject(error);
      }

      // ======================================================
      // NO KNOWN SESSION
      // ======================================================

      console.log(
        '[API] 401 received but no known authenticated session exists.'
      );

      return Promise.reject(error);
    }

    // ========================================================
    // NETWORK ERRORS
    // ========================================================

    if (
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK'
    ) {
      console.error(
        '[API] Network error. Server:',
        API_BASE_URL
      );
    }

    return Promise.reject(error);
  }
);

// ============================================================
// FORM DATA API
// ============================================================

export const formDataApi =
  axios.create({
    baseURL: API_BASE_URL,
    timeout:
      API_CONFIG.TIMEOUT || 30000,
    headers: {
      'Content-Type':
        'multipart/form-data',
      Accept:
        'application/json',
    },
  });

// ============================================================
// FORM DATA REQUEST INTERCEPTOR
// ============================================================

formDataApi.interceptors.request.use(
  (config) => {
    try {
      const token =
        authHelpers.getAccessToken();

      const authType =
        authHelpers.getAuthType();

      if (token) {
        config.headers =
          config.headers || {};

        config.headers.Authorization =
          `Bearer ${token}`;

        console.log(
          `[API] FormData ${authType || 'unknown'} token attached`
        );
      }

      return config;
    } catch (error) {
      console.error(
        '[API] Failed to attach FormData auth:',
        error
      );

      return config;
    }
  },

  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================
// API ERROR HELPER
// ============================================================

export const handleApiError = (
  error
) => {
  if (error.response) {
    const {
      status,
      data,
    } = error.response;

    const message =
      data?.message ||
      data?.error ||
      'Server error occurred';

    return {
      status,
      message,
      data,
      type: 'server',
    };
  }

  if (error.request) {
    return {
      status: 0,
      message:
        'Network error - could not connect to server',
      type: 'network',
    };
  }

  return {
    status: -1,
    message:
      error.message ||
      'Request failed',
    type: 'client',
  };
};

// ============================================================
// EXPORTS
// ============================================================

export {
  API_BASE_URL,
  API_CONFIG,
  TOKEN_KEYS,
};

export default api;

